import {
	STAFF_OR_ABOVE,
	STUDENT_CAREER_CLUSTERS,
	STUDENT_EMPLOYMENT_TYPES,
	STUDENT_TIER_LEVELS,
	type StudentEmploymentType,
	StudentLifecycleStatus,
	UserRole,
} from "@excel-center/shared";

/** Sentinel ListBox id for the clearable "None" option in nullable Selects. */
export const NONE_KEY = "__none__";

export const GRADUATION_MONTHS = [
	{ value: 1, label: "January" },
	{ value: 2, label: "February" },
	{ value: 3, label: "March" },
	{ value: 4, label: "April" },
	{ value: 5, label: "May" },
	{ value: 6, label: "June" },
	{ value: 7, label: "July" },
	{ value: 8, label: "August" },
	{ value: 9, label: "September" },
	{ value: 10, label: "October" },
	{ value: 11, label: "November" },
	{ value: 12, label: "December" },
] as const;

export const EMPLOYMENT_TYPES = STUDENT_EMPLOYMENT_TYPES;
export type EmploymentType = StudentEmploymentType;

export const TIER_LEVELS = STUDENT_TIER_LEVELS;

export const CAREER_CLUSTERS = STUDENT_CAREER_CLUSTERS;

export const STUDENT_STATUSES = [
	StudentLifecycleStatus.ACTIVE,
	StudentLifecycleStatus.INACTIVE,
	StudentLifecycleStatus.ALUMNI,
	StudentLifecycleStatus.WITHDRAWN,
] as const;
export type StudentStatusValue = (typeof STUDENT_STATUSES)[number];

export const APPROVABLE_ROLES = [UserRole.STUDENT, UserRole.STAFF] as const;
export type ApprovableRole = (typeof APPROVABLE_ROLES)[number];

export const STAFF_OR_ABOVE_ROLES = STAFF_OR_ABOVE;

/**
 * Canonical certification/program-type options (EXCLCTR-6). Sourced from the
 * SIS program list, deduplicated and with near-duplicate spellings merged
 * (case, "&" vs "and", token order). Used as a standardized dropdown to keep
 * data entry consistent; the DB column stays free `text`.
 */
export const CERTIFICATION_PROGRAMS = [
	"Accounting",
	"Advanced STEM Applications",
	"Agribusiness, Accounting, Banking & Finance",
	"Agribusiness, Human Resources Management",
	"Agricultural Engineering and Applied Technologies, Welding",
	"Agricultural Engineering, Industrial, and Mechanical Systems",
	"Agricultural Engineering, Industrial, and Mechanical Systems, Automotive Maintenance and Light Repair, Industrial Maintenance Technology, MEP Systems",
	"Agricultural Engineering, Industrial, and Mechanical Systems, Aviation, Environmental and Natural Resource Management, Horticulture Science, Veterinary and Animal Science, Criminal Justice",
	"Agricultural Engineering, Industrial, and Mechanical Systems, Mechatronics, Industrial Maintenance Technology, MEP Systems",
	"Agricultural Engineering, Industrial, and Mechanical Systems, MEP Systems",
	"Agricultural Engineering, Industrial, and Mechanical Systems, Welding, Mechatronics, Industrial Maintenance Technology, Machining Technology, Automotive Maintenance and Light Repair, MEP Systems, Residential & Commercial Construction, Structural Systems",
	"All",
	"All Information Technology Programs",
	"Architectural & Engineering Design, Advanced STEM Applications, BioSTEM, Engineering Technology",
	"Architectural & Engineering Design, Interior Design",
	"Audio/Visual Production",
	"Automotive Collision Repair",
	"Automotive Maintenance & Light Repair",
	"Automotive Maintenance & Light Repair, Automotive Collision Repair",
	"Aviation Flight",
	"Barbering",
	"BioSTEM",
	"Business Management, Office Management",
	"Coding",
	"Coding, Web Design",
	"Cosmetology",
	"Criminal Justice & Corrective Services",
	"Culinary Arts",
	"Cybersecurity",
	"Cybersecurity, Networking Systems",
	"Diagnostic Services",
	"Dietetics and Nutrition",
	"Digital Arts & Design",
	"Early Childhood Education Careers",
	"Early Childhood Education Careers (PreK-4)",
	"Emergency Services",
	"Emergency Services, Fire Management Services",
	"Environmental and Natural Resource Management",
	"Food Science, Culinary Arts",
	"Horticulture Science",
	"Hospitality & Tourism Management",
	"Human and Social Sciences",
	"Industrial Maintenance Technology",
	"Machining Technology",
	"Machining Technology, Industrial Maintenance Technology, Mechatronics, Welding",
	"Marketing Management, Entrepreneurship",
	"Mechatronics",
	"Mechatronics, Industrial Maintenance Technology",
	"MEP Systems",
	"MEP Systems, Industrial Maintenance Technology",
	"MEP Systems, Residential & Commercial Construction, Structural Systems",
	"MEP Systems, Structural Systems, Residential & Commercial Construction",
	"Networking Systems",
	"Nursing Services",
	"Office Management",
	"Office Management, Business Management",
	"Office Management, Business Management, Supply Chain Management, Marketing Management, Entrepreneurship",
	"Office Management, Business Management, Supply Chain Management, Marketing Management, Entrepreneurship, Accounting, Banking & Finance",
	"Office Management, Marketing Management, Entrepreneurship",
	"Residential & Commercial Construction",
	"Sport & Human Performance",
	"Structural Systems",
	"Supply Chain Management",
	"Teaching as a Profession (TAP)",
	"Technology",
	"Technology, Engineering, BioSTEM, Advanced STEM Applications",
	"Therapeutic Services",
	"Veterinary and Animal Science",
	"Web Design",
	"Web Design, Cybersecurity, Coding, Networking Systems",
	"Welding",
	"Welding, MEP Systems, Structural Systems, Residential & Commercial Construction",
] as const;

export type CertificationProgram = (typeof CERTIFICATION_PROGRAMS)[number];
